// src/hooks/useSpeechToText.js
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useSpeechToText wraps the browser Web Speech API for speech-to-text.
 *
 * It exposes { supported, listening, interim, error, start, stop } where:
 *   supported  whether the browser exposes SpeechRecognition.
 *   listening  whether capture is currently active.
 *   interim    the in-progress (not yet finalized) transcript, for live display.
 *   error      the last error code, or null.
 *   start      start(onFinal) begins capture; onFinal(text) fires once per
 *              finalized chunk (trimmed, non-empty).
 *   stop       ends capture and clears interim.
 *
 * @param {{ lang?: string }} [options]
 */
export default function useSpeechToText(options = {}) {
  const { lang = "en-US" } = options;

  const SpeechRecognition =
    typeof window !== "undefined"
      ? window.SpeechRecognition || window.webkitSpeechRecognition
      : undefined;
  const supported = Boolean(SpeechRecognition);

  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState(null);

  // Holds the active recognition instance. Null when nothing is running.
  const recRef = useRef(null);

  const stop = useCallback(() => {
    const rec = recRef.current;
    if (rec) {
      // Calling stop() will trigger onend, which performs the teardown.
      rec.stop();
    }
    setListening(false);
    setInterim("");
  }, []);

  const start = useCallback(
    (onFinal) => {
      if (!supported) {
        return;
      }
      // Guard double-start: if a recognition is already running, no-op.
      if (recRef.current) {
        return;
      }

      const rec = new SpeechRecognition();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = lang;

      rec.onresult = (event) => {
        let nextInterim = "";
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const result = event.results[i];
          const transcript = result[0].transcript;
          if (result.isFinal) {
            const finalText = transcript.trim();
            if (finalText && typeof onFinal === "function") {
              onFinal(finalText);
            }
          } else {
            nextInterim += transcript;
          }
        }
        setInterim(nextInterim);
      };

      rec.onerror = (event) => {
        setError(event.error);
        setListening(false);
      };

      rec.onend = () => {
        recRef.current = null;
        setListening(false);
        setInterim("");
      };

      recRef.current = rec;
      setError(null);
      try {
        rec.start();
        setListening(true);
      } catch (err) {
        recRef.current = null;
        setListening(false);
        setError((err && err.message) || "start-failed");
      }
    },
    [supported, SpeechRecognition, lang]
  );

  // Clean up (stop the recognizer) on unmount.
  useEffect(() => {
    return () => {
      const rec = recRef.current;
      if (rec) {
        rec.onend = null;
        rec.onerror = null;
        rec.onresult = null;
        rec.stop();
        recRef.current = null;
      }
    };
  }, []);

  return { supported, listening, interim, error, start, stop };
}
